// Runtime mode hooks — live/test toggle for the whole admin platform.
//
// Backend contract:
//   GET  /api/settings/runtime-mode  → { mode, updated_at, updated_by_email }
//   POST /api/settings/runtime-mode  → body { mode, confirm_phrase }
//
// Refetch every 30 seconds so a second admin's mode flip propagates to
// every open tab quickly. On flip success we invalidate the entire
// query cache — mode determines which Stripe/Postmark/TikTok credential
// every other query reads through, so the whole dashboard needs to
// re-fetch against the new mode's data.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api, type RuntimeMode, type RuntimeModeResponse } from './api';
import { queryKeys } from './query';

export interface UseRuntimeModeResult {
  mode: RuntimeMode | null;
  updated_at: string | null;
  updated_by_email: string | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  raw: UseQueryResult<RuntimeModeResponse>;
}

/**
 * Subscribe to the current runtime mode. Refetches every 30s so other
 * admins' flips propagate fast across open tabs.
 */
export function useRuntimeMode(): UseRuntimeModeResult {
  const q = useQuery({
    queryKey: queryKeys.settings.runtimeMode(),
    queryFn: () => api.settings.runtimeMode.get(),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 15_000,
  });
  return {
    mode: q.data?.mode ?? null,
    updated_at: q.data?.updated_at ?? null,
    updated_by_email: q.data?.updated_by_email ?? null,
    isLoading: q.isPending,
    isError: q.isError,
    error: q.error,
    raw: q,
  };
}

/**
 * Flip the global runtime mode. After success, invalidates ALL queries
 * so the whole dashboard re-fetches against the new mode's keys.
 */
export function useFlipMode(): UseMutationResult<
  RuntimeModeResponse,
  Error,
  { mode: RuntimeMode; confirm_phrase: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.settings.runtimeMode.flip(body),
    onSuccess: (data) => {
      // Optimistically seed the mode query so the banner flips immediately.
      queryClient.setQueryData(queryKeys.settings.runtimeMode(), data);
      // Then nuke everything — mode shifts every downstream credential.
      void queryClient.invalidateQueries();
    },
  });
}
